import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { DRAG_BOARD, DRAG_SET, ZOOM } from '../../redux/actions/board';
import Node from '../Node';
import NodeConnection from '../NodeConnection';
import WelcomeNode from '../WelcomeNode';
import { MOVEMENT_SPEEDS } from './config';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import './style.css';

const Board = ({ disabled, source }) => {

    const containerRef = useRef( null ),
    dispatch = useDispatch(),
    gridRef = useRef( null ),
    gridClasses = [ 'coe__board__grid' ],
    theme = useTheme(),
    isMobile = useMediaQuery( theme.breakpoints.down('md') ),
    { connections, nodes, zoom, viewPos, magnifications } = useSelector( state => state.board ),
    [ dragging, setDragging ] = useState( null );

    /**
     * Keep at the top
     * @name updatePositions
     * @function
     * @description Updates the positions of the viewbox and the background grid
     * @param {number} clientX Next X position
     * @param {number} clientY Next Y position
     */
    const updatePositions = useCallback( ( clientX, clientY ) => {

        // do this to prevent jumping
        const SPEED = ( MOVEMENT_SPEEDS[ zoom ] );

        // directional values will be +/- 1 
        let x = ( window.oldX - clientX ),
        y = ( window.oldY - clientY ),
        bgX = parseInt( gridRef.current.style.backgroundPositionX.replace( 'px', '' ) ) - x,
        bgY = parseInt( gridRef.current.style.backgroundPositionY.replace( 'px', '' ) ) - y;

        containerRef.current.viewBox.baseVal.x += ( x * SPEED );
        containerRef.current.viewBox.baseVal.y += ( y * SPEED );

        // this needs work
        gridRef.current.style.backgroundPosition = `${ bgX  }px ${ bgY }px`;
        
        window.oldX = clientX;
        window.oldY = clientY;

    }, [ containerRef, gridRef, zoom ]);

    /**
     * @name handleMouseMove
     * @function
     * @description Handles moving of the board
     */
    const handleMouseMove = useCallback( e => {
        updatePositions( e.clientX, e.clientY );
    }, [ updatePositions ]);

    /**
     * @name handleScroll
     * @function
     * @description Binds scroll wheel to zoom functionality
     * @param {EventListenerObject} e 
     */
    const handleScroll = useCallback( ({ target, deltaY }) => {
        if( containerRef.current.contains( target ) ) dispatch({ type: ZOOM, payload: ( deltaY < 0 ) });
    }, [ dispatch, containerRef ]);

    /**
     * @name handleTouchMove
     * @function
     * @description handles touch gestures
     */
    const handleTouchMove = useCallback( ({ stopPropagation, changedTouches }) => {

        // change screen position
        if( changedTouches.length === 1 ){
            const { clientX, clientY } = changedTouches[ 0 ];
            updatePositions( clientX, clientY );
        }

    }, [ updatePositions ]);

    /**
     * @name mobileOverscroll
     * @function
     * @description Prevents touch bubbling for overscroll issues
     */
    const mobileOverscroll = useCallback( e => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    /**
     * @name toggleDrag
     * @function
     * @description Toggles the state of the user dragging
     * @param {boolean} state 
     * @returns {function}
     */
    const toggleDrag = state => e => {

        // if dragging and the target is not the current element
        if( state && e.target !== containerRef.current ){
            // do nothing lol... its just easier to write the logic this way.
        } else {
            if( !state ){
                window.oldX = null;
                window.oldY = null;
                if( dragging ) setDragging( false );
            } else {
                const touches = e.changedTouches;

                // mouse
                if( !touches ){
                    window.oldX = e.clientX;
                    window.oldY = e.clientY;

                // phone
                } else {
                    window.oldX = touches[ 0 ].clientX;
                    window.oldY = touches[ 0 ].clientY;
                }

                if( !dragging ) setDragging( true );
            }
        }
    }

    // disable mouse events for things when dragging the board
    useEffect(() => {
        if( dragging !== null ) dispatch({ type: DRAG_SET, payload: dragging });
    }, [ dragging, dispatch ]);

    // add event listeners to document and window to prevent scroll bubbling in mobile
    useEffect(() => {
        window.addEventListener( 'touchmove', mobileOverscroll, { passive: false });
        document.addEventListener( 'touchmove', mobileOverscroll, { passive: false });
        return () => {
            window.removeEventListener( 'touchmove', mobileOverscroll, { passive: false });
            document.removeEventListener( 'touchmove', mobileOverscroll, { passive: false });
        }
    }, [ mobileOverscroll ]);

    // dragging options
    useEffect(() => {
        const { current } = containerRef;

        // add event listener when dragging begins
        if( dragging ){
            if( !isMobile ){
                current?.addEventListener( 'mousemove', handleMouseMove, true );
            } else {
                current?.addEventListener( 'touchmove', handleTouchMove, { passive: true });
            }

        // commit coords
        } else if( dragging !== null ){
            dispatch({ type: DRAG_BOARD, payload: { x: current?.viewBox.baseVal.x || 0, y: current?.viewBox.baseVal.y || 0 } });
            setDragging( null )
        }

        return () => {
            if( !isMobile ){
                current?.removeEventListener( 'mousemove', handleMouseMove, true );
            } else {
                current?.removeEventListener( 'touchmove', handleTouchMove, { passive: true });                
            }
        }
    }, [ containerRef, dispatch, dragging, handleMouseMove, handleTouchMove, isMobile, setDragging,  ]);

    // bind scroll to board
    useEffect(() => {
        const options = { capture: true, passive: true };
        document.addEventListener( 'wheel', handleScroll, options );
        return () => {
            document.removeEventListener( 'wheel', handleScroll, options );
        }
    }, [ handleScroll ]);

    // variations
    gridClasses.push( gridClasses[ 0 ] + `--${ ( zoom + 1 ) }x` );

    // don't render the board until its ready
    if( magnifications.length === 0 ) return null;

    return (
        <>
            <div className={ gridClasses.join( ' ' ) } ref={ gridRef } style={{ backgroundPosition: '0 0' }} />
            <svg 
                className="coe__board"
                onTouchCancel={ isMobile ? toggleDrag( false ) : null }
                onTouchEnd={ isMobile ? toggleDrag( false ) : null }
                onTouchStart={ isMobile ? toggleDrag( true ) : null }
                onMouseDown={ !isMobile ? toggleDrag( true ) : null } 
                onMouseUp={ !isMobile ? toggleDrag( false ) : null }
                onMouseOut={ !isMobile ? toggleDrag( false ) : null }
                ref={ containerRef }
                viewBox={ `${ viewPos.x } ${ viewPos.y } ${ magnifications[ zoom ].width } ${ magnifications[ zoom ].height }` } 
                xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <filter id="coe__board__def__shadow" width="200%" height="200%">
                            <feGaussianBlur in="SourceAlpha" stdDeviation="3"/> 
                            <feOffset dx="0" dy="5"/>
                            <feComponentTransfer>
                                <feFuncA type="linear" slope="0.5"/>
                            </feComponentTransfer>
                            <feMerge> 
                                <feMergeNode/>
                                <feMergeNode in="SourceGraphic"/> 
                            </feMerge>
                        </filter>
                    </defs>
                {
                    Object.keys( source?.connections || connections ).map(
                        ( id, index ) => {

                            const connection = source?.connections[ id ] || connections[ id ];

                            return (
                                <NodeConnection 
                                    disabled={ disabled }
                                    id={ id } 
                                    key={ 'connection-' + index }
                                    idA={ connection[ 0 ] } 
                                    idB={ connection[ 1 ] } />
                            )
                        }
                    )
                }
                {
                    Object.keys( source?.nodes || nodes ).map(
                        ( id, index ) => {

                            const node = source?.nodes[ id ] || nodes[ id ];

                            return (
                                <Node disabled={ disabled } { ...node } key={ 'node-' + index } />
                            )
                        }
                    ) 
                }
                {
                    Object.keys( source?.nodes || nodes ).length === 0 ?
                        <WelcomeNode /> :
                        null
                }
            </svg>
        </>
    )
}

export default Board;